import {HolidayDayDuration} from "@/shared/timrTypes";

export enum IndividualHoliday {
	CHRISTMAS = "christmas",
	NEW_YEARS = "newYears"
}

export interface HolidayIndividualDayChangeDetail {
	type: IndividualHoliday;
	value: HolidayDayDuration;
}

export interface HolidayCalculator {
	includesNewYearsEve: boolean;
	includesChristmasEve: boolean;
}

const template =
	`<div class="form-group" data-holiday="${IndividualHoliday.CHRISTMAS}">
		<div class="mt-10">${messages["holidayCalendar.individual.christmas.eve"]}</div>
		<div>
			<label class="radio-inline">
				<input type="radio" data-holiday-type="christmas" value="${HolidayDayDuration.NONE}">
				${messages["holidayCalendar.individual.none"]}
			</label>
			<label class="radio-inline">
				<input type="radio" data-holiday-type="christmas" value="${HolidayDayDuration.FULL_DAY}">
				${messages["holidayCalendar.individual.full.day"]}
			</label>
			<label class="radio-inline">
				<input type="radio" data-holiday-type="christmas" value="${HolidayDayDuration.HALF_DAY}">
				${messages["holidayCalendar.individual.half.day"]}
			</label>
		</div>
	</div>
	<div class="form-group" data-holiday="${IndividualHoliday.NEW_YEARS}">
		<div class="mt-10">${messages["holidayCalendar.individual.new.years.eve"]}</div>
		<div>
			<label class="radio-inline">
				<input type="radio" data-holiday-type="newYears" value="${HolidayDayDuration.NONE}">
				${messages["holidayCalendar.individual.none"]}
			</label>
			<label class="radio-inline">
				<input type="radio" data-holiday-type="newYears" value="${HolidayDayDuration.FULL_DAY}">
				${messages["holidayCalendar.individual.full.day"]}
			</label>
			<label class="radio-inline">
				<input type="radio" data-holiday-type="newYears" value="${HolidayDayDuration.HALF_DAY}">
				${messages["holidayCalendar.individual.half.day"]}
			</label>
		</div>
	</div>`;

export class HolidayIndividualDays extends HTMLElement {
	public static readonly EVENT_DAY_SELECTION_CHANGE = "holiday-individual-day-change";

	public updateHoliday(type: IndividualHoliday, options: { value?: HolidayDayDuration; include?: boolean }) {
		const {value, include} = options;

		if (value !== undefined) {
			this.setHolidayValue(type, value);
			this.setDatasetValue(type, value);
		}

		if (include !== undefined) {
			this.setHolidayVisibility(type, !include);
		}
	}

	/**
	 * Get the initial values of HolidayCalculator of the jsp
	 * This used to update the visibility of the component for the first time, without having to retrieve (prevent flickering)
	 */
	public getInitialCalculatorInfo(): HolidayCalculator | null {
		const {initialIncludesChristmas, initialIncludesNewYears} = this.dataset;

		if (initialIncludesChristmas === undefined && initialIncludesNewYears === undefined) {
			return null;
		}

		return {
			includesChristmasEve: initialIncludesChristmas === "true",
			includesNewYearsEve: initialIncludesNewYears === "true"
		};
	}

	public connectedCallback(): void {
		this.render();
	}

	private render() {
		this.innerHTML = template;

		for (const type of Object.values(IndividualHoliday)) {
			this.initializeHolidayGroup(type);
			this.applyHolidayVisibility(type);
		}

		const inputs = this.querySelectorAll<HTMLInputElement>("input");
		for (const input of inputs) {
			input.addEventListener("change", this.handleInputChange);
		}
	}

	private initializeHolidayGroup(type: IndividualHoliday) {
		const name = this.getHolidayName(type);
		const selectedValue = this.getHolidayValue(type);
		const inputs = this.querySelectorAll<HTMLInputElement>(`input[data-holiday-type="${type}"]`);

		for (const input of inputs) {
			input.name = name;
			input.checked = input.value as HolidayDayDuration === selectedValue;
		}
	}

	/**
	 * Retrieves the name of the holiday type for use in the radio input name attribute.
	 * This allows configuring the effective name attribute of the inputs for the passed value to the server
	 * @param type
	 * @private
	 */
	private getHolidayName(type: IndividualHoliday): string {
		const datasetNameKey = `${type}Name`;

		// name to be used for the radio input attribute comes from dataset; fall back when the attribute is absent
		return this.dataset[datasetNameKey] ?? datasetNameKey;
	}

	private getHolidayValue(type: IndividualHoliday): HolidayDayDuration {
		// Persisted value comes from dataset; fall back when the attribute is absent.
		const datasetValueKey = `${type}Value`;
		const datasetValue = this.dataset[datasetValueKey] as HolidayDayDuration;

		if (datasetValue) {
			return datasetValue;
		}

		// fall back to NONE when no value is set
		return HolidayDayDuration.NONE;
	}

	private setHolidayValue(type: IndividualHoliday, value: HolidayDayDuration) {
		// Keep DOM state (radio buttons) aligned with the imperative API.
		const inputs = this.getHolidayInputs(type);

		if (!inputs || !inputs.length) {
			return;
		}

		for (const input of inputs) {
			input.checked = input.value as HolidayDayDuration === value;
		}
	}

	private setDatasetValue(type: IndividualHoliday, value: HolidayDayDuration) {
		this.dataset[`${type}Value`] = value;
	}

	private setHolidayVisibility(type: IndividualHoliday, visible: boolean) {
		this.setVisibilityFlag(type, visible);
		this.applyHolidayVisibility(type);
	}

	private applyHolidayVisibility(type: IndividualHoliday) {
		// Some tenants hide individual overrides entirely; display toggling is cheaper than re-rendering.
		const group: HTMLElement = this.querySelector(`[data-holiday="${type}"]`);
		const visible = this.getVisibilityFlag(type);

		if (!group) {
			return;
		}

		if (visible === undefined) {
			return;
		}

		group.style.display = visible ? "" : "none";
	}

	/**
	 * Handle changes to input elements and dispatches a custom event with the updated holiday data.
	 *
	 * This function is typically used as an event handler for input changes in the context of managing holiday data.
	 * It extracts the holiday type and duration value from the event's target, updates the corresponding dataset,
	 * and dispatches a custom event to notify other parts of the application about the update.
	 *
	 * The custom event dispatched includes the type and value of the holiday data that was changed.
	 *
	 * @param {Event} event - The event triggered by the input change.
	 * @throws Will terminate early if the event target is not an HTMLInputElement or if the `holidayType` dataset attribute is not provided.
	 */
	private handleInputChange = (event: Event) => {
		const target = event.target as HTMLInputElement;
		if (!target) {
			return;
		}

		const type = target.dataset.holidayType as IndividualHoliday;
		if (!type) {
			return;
		}

		const value = target.value as HolidayDayDuration;
		this.setDatasetValue(type, value);

		this.dispatchEvent(new CustomEvent<HolidayIndividualDayChangeDetail>(HolidayIndividualDays.EVENT_DAY_SELECTION_CHANGE, {
			detail: {
				type,
				value
			},
			bubbles: true
		}));
	}

	private getHolidayInputs(type: IndividualHoliday): NodeListOf<HTMLInputElement> {
		const name = this.getHolidayName(type);
		return this.querySelectorAll<HTMLInputElement>(`input[name="${name}"]`);
	}

	private getVisibilityFlag(type: IndividualHoliday): boolean | undefined {
		const value = this.dataset[`${type}Visible`];

		if (value === undefined) {
			return undefined;
		}

		return value === "true";
	}

	private setVisibilityFlag(type: IndividualHoliday, visible: boolean) {
		this.dataset[`${type}Visible`] = String(visible);
	}
}
